import { useLingui } from "@lingui/react/macro";
import type { Bot, Group, ThreadSnapshot } from "@rakazo/contracts";
import { BotAvatar, Button, GroupAvatar } from "@rakazo/ui-web";
import { Menu, Phone } from "lucide-react";
import { IconTooltip } from "../../components/IconTooltip";
import { desktopBridge, windowChromeKind } from "../../lib/desktop";
import { WindowChrome } from "../WindowChrome";

/** The chat's title bar: navigation on phones, the chat's name, and its actions. */
export function ChatHeader({
  inGroup,
  active,
  activeGroup,
  activeSnapshot,
  leader,
  phoneCallsEnabled,
  inCall,
  panelOpen,
  panelPermanent = false,
  windowControlsEnabled = true,
  onOpenNavigation,
  onTogglePanel,
  onOpenLeader,
  onCall,
}: {
  inGroup: boolean;
  active: Bot | undefined;
  activeGroup: Group | undefined;
  activeSnapshot: ThreadSnapshot | null;
  /** The team's leader, offered as a private chat from the group it runs. */
  leader: Bot | undefined;
  phoneCallsEnabled: boolean;
  inCall: boolean;
  panelOpen: boolean;
  /** Desktop keeps the right panel open, so its title is static and the window buttons live there. */
  panelPermanent?: boolean;
  windowControlsEnabled?: boolean;
  onOpenNavigation: () => void;
  onTogglePanel: () => void;
  onOpenLeader: (bot: Bot) => void;
  onCall: (bot: Bot) => void;
}) {
  const { t } = useLingui();
  const desktop = desktopBridge();
  const showControls =
    windowControlsEnabled && desktop !== undefined && windowChromeKind(desktop) === "controls";
  const titleContent = (
    <>
      {inGroup ? (
        <GroupAvatar members={activeSnapshot?.members ?? activeGroup?.members ?? []} size={24} />
      ) : active ? (
        <BotAvatar
          avatarSet={active.avatarSet}
          color={active.color}
          identity={active.id}
          size={24}
          status={active.status}
        />
      ) : null}
      <span className="min-w-0 truncate text-ui-base font-medium text-foreground" dir="auto">
        {inGroup
          ? (activeGroup?.name ?? activeSnapshot?.groupName ?? t`Company`)
          : (active?.name ?? t`Select a bot`)}
      </span>
    </>
  );
  return (
    <header className="relative flex h-12 shrink-0 items-center gap-1 border-b border-border/50 px-2">
      <div className="app-drag flex min-w-0 flex-1 items-center gap-1">
        <Button
          variant="ghost"
          size="icon-md"
          aria-label={t`Open navigation`}
          onClick={onOpenNavigation}
          className="app-no-drag text-foreground-subtle hover:text-foreground md:hidden"
        >
          <Menu strokeWidth={1.7} className="size-4" />
        </Button>
        {panelPermanent ? (
          <div
            data-testid="bot-settings-trigger"
            className="flex h-8 min-w-0 items-center gap-2 rounded-md px-1.5 text-ui-base font-medium"
          >
            {titleContent}
          </div>
        ) : (
          <Button
            variant="ghost"
            data-testid="bot-settings-trigger"
            aria-expanded={panelOpen}
            onClick={onTogglePanel}
            className="app-no-drag h-8 min-w-0 gap-2 rounded-md px-1.5 text-ui-base font-medium hover:bg-hover"
          >
            {titleContent}
          </Button>
        )}
      </div>
      <div className="app-no-drag flex shrink-0 items-center gap-1">
        {inGroup && leader ? (
          <IconTooltip label={t`Chat with ${leader.name}`}>
            <Button
              variant="ghost"
              size="sm"
              data-testid="team-leader-button"
              aria-label={t`Chat with ${leader.name}`}
              onClick={() => onOpenLeader(leader)}
              className="app-no-drag gap-1.5 px-1.5 text-foreground-subtle hover:text-foreground"
            >
              <BotAvatar
                avatarSet={leader.avatarSet}
                color={leader.color}
                identity={leader.id}
                size={20}
                status={leader.status}
              />
              <span className="max-w-24 truncate text-ui-sm">{leader.name}</span>
            </Button>
          </IconTooltip>
        ) : null}
        {phoneCallsEnabled && !inGroup && active ? (
          <IconTooltip label={inCall ? t`Open call` : t`Call`}>
            <Button
              variant="ghost"
              size="icon-md"
              data-testid="call-button"
              aria-label={inCall ? t`Open call` : t`Call`}
              onClick={() => onCall(active)}
              data-active={inCall ? "" : undefined}
              className="app-no-drag text-foreground-subtle hover:text-foreground"
            >
              <Phone strokeWidth={1.6} className="size-4" />
            </Button>
          </IconTooltip>
        ) : null}
        {showControls ? <WindowChrome /> : null}
      </div>
    </header>
  );
}
